/**
 * Subscription Context Provider
 * Provides subscription state and paywall controls to the entire app
 */
import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import useSubscription from '../hooks/useSubscription';
import Paywall from '../components/Paywall';
import ProUpgradeNotice from '../components/ProUpgradeNotice';
import { setSubscriptionRequiredHandler } from '../utils/api';
import { toast } from 'sonner';

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const subscription = useSubscription();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState(null);
  const [paywallCallback, setPaywallCallback] = useState(null);

  // Centered "you've crossed into a Pro-only feature" notice. Shown BEFORE
  // the paywall for specific 402 codes (e.g. planning_around_logged_meals)
  // so the user understands exactly which boundary they crossed instead of
  // seeing a generic failed-state toast followed by the paywall.
  const [proNotice, setProNotice] = useState({ open: false, title: '', message: '', feature: null });

  /**
   * Check if user can access a feature.
   * If not, show paywall and return false.
   * If yes, return true.
   */
  const requireFeature = useCallback((feature, onSuccess = null) => {
    if (subscription.canAccess(feature)) {
      return true;
    }
    
    // Show paywall
    setPaywallFeature(feature);
    setPaywallCallback(() => onSuccess);
    setPaywallOpen(true);
    return false;
  }, [subscription]);

  /**
   * Show paywall for a specific feature
   */
  const showPaywall = useCallback((feature = null) => {
    setPaywallFeature(feature);
    setPaywallOpen(true);
  }, []);

  // Register a global handler so any API call that returns 402
  // (subscription_required / daily_limit_reached) automatically opens the
  // paywall. Keeps api.js decoupled from React.
  //
  // Split-brain recovery: if a 402 arrives while the frontend believes the
  // user is subscribed (e.g., stale RC SDK customerInfo cache after a
  // sandbox subscription ended), we DON'T just open the paywall — it would
  // immediately auto-close because isSubscribed is still true locally,
  // leaving the user with no recovery UI. Instead we trigger a server-side
  // reconcile against RC. If RC agrees the user is Pro, we toast a retry
  // hint and skip the paywall. If RC says the user is truly free, we
  // forcibly downgrade local state and open the paywall normally.
  //
  // Refs let the handler read the latest subscription state without
  // re-registering on every state change.
  const subscriptionRef = useRef(subscription);
  useEffect(() => { subscriptionRef.current = subscription; }, [subscription]);
  // Prevent multiple concurrent reconciles from a burst of 402s.
  const reconcileInFlightRef = useRef(false);

  useEffect(() => {
    setSubscriptionRequiredHandler(async ({ code, detail }) => {
      const sub = subscriptionRef.current;

      // Planning-around-logged-meals is a distinct Pro boundary from the
      // generic subscription_required / daily_limit_reached gates. Show a
      // dedicated centered notice that explains the boundary clearly
      // BEFORE the paywall opens — this replaces the previous "Failed to
      // generate meals" toast flicker that confused users.
      if (code === 'planning_around_logged_meals_requires_pro') {
        setProNotice({
          open: true,
          title: 'This is a Fitxess Pro feature',
          message: detail?.message
            || 'You already have food logged for this day. Planning around logged meals is a Fitxess Pro feature.',
          feature: 'can_regenerate_unlimited',
        });
        return;
      }

      // Planning a FUTURE day is Pro; today stays free. Show a clear notice
      // that names the boundary before the paywall.
      if (code === 'future_date_requires_pro') {
        setProNotice({
          open: true,
          title: 'Plan ahead with Fitxess Pro',
          message: detail?.message
            || 'Free plans cover today. Upgrade to Fitxess Pro to plan any future day.',
          feature: 'can_generate_weekly_plan',
        });
        return;
      }

      if (code === 'daily_limit_reached') {
        toast.message(detail?.message || 'Daily plan limit reached.');
        setPaywallFeature(null);
        setPaywallOpen(true);
        return;
      }

      // Standard subscription_required path. Check for split-brain BEFORE
      // opening the paywall.
      if (sub?.isSubscribed && !reconcileInFlightRef.current) {
        reconcileInFlightRef.current = true;
        try {
          const result = await sub.reconcileBackend();
          const status = result?.reconcile_status;

          if (status === 'ok_paid' || status === 'skipped_override') {
            // Backend now confirms Pro. Refresh local status from backend
            // so future calls won't 402, and prompt user to retry.
            try { await sub.refresh(); } catch (_) { /* ignore */ }
            toast.message('Subscription synced — please try that again.');
            return; // do NOT open paywall
          }

          if (status === 'ok_free') {
            // Backend confirmed user truly is not Pro right now. Override
            // the smart-merge guard so local state matches reality, then
            // open paywall normally.
            sub.forceDowngradeToFree();
            setPaywallFeature(null);
            setPaywallOpen(true);
            return;
          }

          // Degraded reconcile (RC unreachable). Open the paywall but
          // surface the sync issue so the user knows why.
          toast.error('Couldn\'t verify your subscription. Try Sync Subscription in Settings.');
          setPaywallFeature(null);
          setPaywallOpen(true);
          return;
        } finally {
          reconcileInFlightRef.current = false;
        }
      }

      // Default path: frontend already knows user is free, OR a reconcile
      // is already in flight — just open the paywall.
      setPaywallFeature(null);
      setPaywallOpen(true);
    });
    return () => setSubscriptionRequiredHandler(null);
  }, []);

  /**
   * Hide paywall
   */
  const hidePaywall = useCallback(() => {
    setPaywallOpen(false);
    setPaywallFeature(null);
    setPaywallCallback(null);
  }, []);

  /**
   * Handle successful purchase
   */
  const handlePurchaseComplete = useCallback(() => {
    hidePaywall();
    if (paywallCallback) {
      paywallCallback();
    }
  }, [hidePaywall, paywallCallback]);

  const value = {
    ...subscription,
    requireFeature,
    showPaywall,
    hidePaywall,
    paywallOpen,
    paywallFeature,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
      <ProUpgradeNotice
        isOpen={proNotice.open}
        title={proNotice.title}
        message={proNotice.message}
        onUpgrade={() => {
          // Hand off to paywall — close notice first so the paywall has a
          // clean stack (otherwise the notice's z-[60] sits over the
          // paywall's z-50).
          const feature = proNotice.feature;
          setProNotice({ open: false, title: '', message: '', feature: null });
          setPaywallFeature(feature);
          setPaywallOpen(true);
        }}
        onClose={() => setProNotice({ open: false, title: '', message: '', feature: null })}
      />
      <Paywall
        isOpen={paywallOpen}
        onClose={hidePaywall}
        feature={paywallFeature}
        onPurchaseComplete={handlePurchaseComplete}
      />
    </SubscriptionContext.Provider>
  );
}

/**
 * Hook to access subscription context
 */
export function useSubscriptionContext() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
  }
  return context;
}

export default SubscriptionContext;
